import { Platform } from 'react-native';
import { createTokens } from 'tamagui';

/**
 * ── Putting Scene design tokens (single source of truth) ──────────────
 *
 * This is the ONLY place raw design values (hex colours, px sizes, radii,
 * spacing) may live. Everywhere else consumes these via Tamagui's `$token`
 * syntax — `$brand`, `$textDark`, `$md`, `$base`, `$rounded`, etc. A raw value
 * in a component (e.g. `fontSize={18}`, `color="#5B4FE9"`) is a bug the linter
 * will flag: if a value is worth using, it earns a token here first.
 *
 * Two layers (see https://tamagui.dev/docs/core/tokens):
 *   1. `createTokens({ color, space, radius, borderWidth })` below — the raw
 *      palette and scales. Makes `$brand`, `$textDark`, `$md`… available
 *      anywhere. (Font sizes are NOT here — they live in the font config; see
 *      the `fontSize` note below.)
 *   2. Semantic theme aliases (`$background`, `$borderColor`, `$color`) live in
 *      tamagui.config.ts and POINT at these tokens, so they can differ per
 *      light/dark theme while still resolving to a token value.
 *
 * Font FAMILIES (`$body`, `$hand`) and font WEIGHTS live in `createFont` in
 * tamagui.config.ts, not here — Tamagui models those as font config, not the
 * token categories below. Weight names are re-exported from here as `FontWeight`
 * so the linter has one list to enforce against.
 *
 * `borderWidth` is a CUSTOM token group (not a built-in category), so it's only
 * reachable via the specific-token syntax `borderWidth="$borderWidth.hairline"`
 * and is enforced by the linter, not Tamagui's strict mode. See its note below.
 * ──────────────────────────────────────────────────────────────────────
 */

/**
 * Raw brand palette. `background` / `borderColor` mirror Tamagui's default
 * light-theme neutrals (hsla 97% / 85%) — the designer inherited these rather
 * than inventing new ones, so we keep the exact values as named tokens.
 * The muted text tokens are the honest colours behind the sample's opacity
 * tricks (base text @ 0.6, white @ 0.82).
 */

const brandPalette = {
  brightSnow: '#F8F8F8',
  pinkCarnation: '#E67BB5',
  cherryRose: '#A41C5B',
  onyx: '#0F0F0F',
  gray: '#6C6C6C',
  wine: '#3C0C1E',
} as const;

export const color = {
  brand: brandPalette.cherryRose,
  brandDark: brandPalette.onyx,
  brandGray: brandPalette.gray,
  brandSecondary: brandPalette.pinkCarnation,
  trialBadge: 'rgba(255, 122, 192, 0.5)',

  background: brandPalette.brightSnow,
  backgroundSecondary: 'hsla(0, 0%, 93%, 1)',
  borderColor: 'hsla(0, 0%, 85%, 1)',

  textDark: brandPalette.onyx,

  textDarkMuted: 'rgba(15, 15, 15, 0.7)',

  textWhite: brandPalette.brightSnow,

  textWhiteMuted: 'rgba(248, 248, 248, 0.82)',

  // The one error/danger color — a clear red for invalid-field borders and
  // validation messages (PSInput's error state). A conscious scale addition:
  // the palette had no red, and an error cue can't honestly borrow $brand
  // (purple reads as active/valid). Kept to a single step; darker error/lighter
  // error variants earn their own token only when a call site needs them.
  error: '#DC2626',
  // Its counterpart for confirmations (an applied coupon).
  success: '#219E1F',

  // The modal scrim — onyx at half strength, derived the same way the muted
  // text tokens are. A conscious scale addition, like `error`: the palette had
  // no dimming layer, and PSAlertHost needs one to sit a dialog over the page.
  scrim: 'rgba(15, 15, 15, 0.5)',

  // The trial pass card's glow stop — a conscious addition, like `error`: the
  // palette had no wine tone, and it's specific to that card, not a scale.
  passGradientStart: brandPalette.wine,

  panelTranslucent: 'rgba(255, 255, 255, 0.15)',
  // "No fill" as a real token — several controls (unfilled circle buttons,
  // inactive chips/filters) need a transparent surface, and strict mode rejects
  // the raw "transparent" string on color props.
  transparent: 'transparent',
} as const;

/**
 * Font sizes. Deliberately only three steps — anything outside small/base/large
 * must be added here consciously, not reached for inline. `true` is the value
 * used when a fontSize prop is set without a token (defaults to `base`).
 *
 * NOTE: these are NOT part of `createTokens` — font sizes belong to the FONT
 * config (`createFont({ size })` in tamagui.config.ts), which is what
 * `fontSize="$base"` reads from. They are intentionally kept OUT of the Tamagui
 * `size` token group: that group is for width/height and shares its key set with
 * `zIndex`/`space` in the preset, so renaming it to small/base/large would break
 * that contract and crash `createTamagui`. This export exists so the font config
 * has one source for the values.
 */
export const fontSize = {
  xs: 12,
  small: 14,
  base: 16,
  md: 18,
  large: 20,
  xl: 24,
  xl2: 28,
  xxl: 32,
  veryLarge: 48,
  true: 16,
} as const;

/**
 * Letter spacing, in px. Not a Tamagui token CATEGORY (there is no
 * `letterSpacing` group), so these are read numerically — exactly like `space`
 * is for negative margins, and the linter doesn't police the prop. The style
 * sheet's "tracking 12" on Heading 3 is read as PERCENT of the font size, not
 * 12px: 12% of 12px → 1.44. `tags` is a manual override, not derived from
 * that formula — see the tags DEVIATION note above `type`.
 */
export const letterSpacing = {
  normal: 0,
  h3: 1.44,
  tags: 1.66,
  psButtonToolbar: 1.33,
} as const;

/**
 * Spacing scale for margin / padding / gap. `true` (the bare default) maps to
 * `md`.
 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  true: 12,
} as const;

/**
 * Border radius. `base` is the standard lightly-rounded corner; `full` is the
 * pill / circle radius. `true` (the bare default) maps to `base`.
 */
export const radius = {
  base: 6,
  full: 9999,
  true: 6,
} as const;

/**
 * Border widths — a CUSTOM token group. `borderWidth` isn't one of Tamagui's
 * built-in token categories (color/size/space/radius/zIndex), so it isn't
 * auto-mapped to the `borderWidth` prop and Tamagui's strict mode can't validate
 * it. It's reachable only via the "specific token" syntax naming the category:
 *   `borderWidth="$borderWidth.hairline"`
 * Enforcement of this one is on the ESLint rule, not Tamagui.
 *
 * ── Why the value carries a unit on web and not on native ──
 * Tamagui appends `px` to the CSS variables of the categories it knows are
 * lengths — `shouldTokenCategoryHaveUnits` is a hardcoded Set of size/space/
 * radius — so a CUSTOM category emits `--t-borderWidth-hairline: 1`. Unitless
 * `1` is not a valid `border-width`, so the browser drops the declaration and
 * falls back to the initial value `medium`, i.e. 3px: every hairline in the app
 * rendered three times too thick on web. Native never touches CSS and needs a
 * plain number, so the unit can't just be baked in for both.
 */
const HAIRLINE = 1;

export const borderWidth = {
  hairline: Platform.OS === 'web' ? `${HAIRLINE}px` : HAIRLINE,
} as const;

/**
 * Font weights. The two the design uses. Wired into `createFont` in
 * tamagui.config.ts; exported here so the linter has one canonical list.
 * `medium` (500) is the default weight — the font's `true` sentinel maps to it.
 */
export const FontWeight = {
  medium: '500',
  semibold: '600',
} as const;

/**
 * ── The type roles (designer's style sheet, 2026-08-11) ───────────────
 *
 *   role    size  weight     line-h  tracking  case   where
 *   ─────────────────────────────────────────────────────────────────────
 *   title    24   semibold   32      0         —      moments: the upsell banner
 *   h1       20   semibold   24      0         —      the event name
 *   h2       18   semibold   24      0         —      About / Tickets / Terms
 *   h3       12   semibold   16      1.44      CAPS   "FOR MEMBERS ONLY"
 *   body1    20   medium     28      0         —      prices, the date-box number
 *   body2    16   medium     22      0         —      prose, tier names, meta
 *   body3    14   medium     19      0         —      supporting copy
 *   body4    12   medium     16      0         —      the smallest supporting text
 *   tags     10   semibold   14      1.20      CAPS   every PSBadge
 *
 * THIS map is the hierarchy, not the sizes above it: roles share sizes and
 * separate by weight, case or colour instead (h1/body1 are both 20px, h3/body4
 * both 12px), which a screen setting fontSize by hand can't express.
 *
 * Consumed ONLY by `components/ui/PSText.tsx`, which turns each role into a
 * component (`<PSText.H1>`); the linter bans importing this map anywhere else, so
 * a role can't be half-applied by spreading it onto a bare `<Text>`.
 *
 * Colour is deliberately NOT in a role — the same role appears on the light page
 * and on the Onyx block with opposite text colours, so the call site sets it.
 * body3's "70% opacity" in the sheet is the `$textDarkMuted` COLOUR, not a real
 * `opacity`, so nested text and icons don't inherit a fade.
 *
 * `lineHeight` is explicit per role rather than inherited from the font config,
 * which keys line height by SIZE — h1 is 20/24 and body1 is 20/28, a pair a
 * size-keyed default cannot express.
 *
 * Two DEVIATIONS from the sheet:
 *   - body1's line height is 28, not the 19 specified. 19 sits below the 20px
 *     font size, clipping descenders the moment body1 wraps; in the reference
 *     screens body1 is only ever a one-line price or date number, so it never
 *     showed there. 28 is x1.4, matching body2's x1.38.
 *   - tags is 12/16/1.66, not the sheet's 10/14/1.20 — the dedicated 10px
 *     `fontSize` step was dropped, so tags now shares `$xs` (12px) with
 *     h3/body4, with line height and tracking bumped to match.
 */
export const type = {
  title: {
    fontFamily: '$body',
    fontSize: '$xl2',
    fontWeight: '$semibold',
    lineHeight: 32,
    letterSpacing: letterSpacing.normal,
  },
  subTitle: {
    fontFamily: '$body',
    fontSize: '$xl',
    fontWeight: '$semibold',
    lineHeight: 28,
    letterSpacing: letterSpacing.normal,
  },
  h1: {
    fontFamily: '$body',
    fontSize: '$large',
    fontWeight: '$semibold',
    lineHeight: 24,
    letterSpacing: letterSpacing.normal,
  },
  h2: {
    fontFamily: '$body',
    fontSize: '$md',
    fontWeight: '$semibold',
    lineHeight: 24,
    letterSpacing: letterSpacing.normal,
  },
  h3: {
    fontFamily: '$body',
    fontSize: '$xs',
    fontWeight: '$semibold',
    lineHeight: 16,
    letterSpacing: letterSpacing.h3,
    textTransform: 'uppercase',
  },
  body1: {
    fontFamily: '$body',
    fontSize: '$large',
    fontWeight: '$medium',
    lineHeight: 28,
    letterSpacing: letterSpacing.normal,
  },
  body2: {
    fontFamily: '$body',
    fontSize: '$base',
    fontWeight: '$medium',
    lineHeight: 22,
    letterSpacing: letterSpacing.normal,
  },
  body3: {
    fontFamily: '$body',
    fontSize: '$small',
    fontWeight: '$medium',
    lineHeight: 19,
    letterSpacing: letterSpacing.normal,
  },
  body4: {
    fontFamily: '$body',
    fontSize: '$xs',
    fontWeight: '$medium',
    lineHeight: 16,
    letterSpacing: letterSpacing.normal,
  },
  tags: {
    fontFamily: '$body',
    fontSize: '$xs',
    fontWeight: '$semibold',
    lineHeight: 16,
    letterSpacing: letterSpacing.tags,
    textTransform: 'uppercase',
  },
} as const;

export const tokens = createTokens({
  color,
  space,
  radius,
  borderWidth,
});
